/**
 * Member Enrichment Tab
 * Displays enrichment summary scores, module results, risk flags, and Enrich Now trigger.
 * Used as a tab on MemberProfileNew.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Clock,
  Shield,
  Heart,
  MapPin,
  Activity,
  DollarSign,
  ClipboardCheck,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Loader2,
  Zap,
  Info,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  memberEnrichmentAPI,
  EnrichmentSummary,
  EnrichmentResult,
  RetentionScore,
} from '@/services/api/memberEnrichment';
import ChangeReviewPanel from './ChangeReviewPanel';

interface MemberEnrichmentTabProps {
  memberId: number;
  memberName?: string;
}

// Score color helpers
const scoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-blue-600 dark:text-blue-400';
  if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const scoreBg = (score: number) => {
  if (score >= 80) return 'bg-green-100 dark:bg-green-900/30';
  if (score >= 60) return 'bg-blue-100 dark:bg-blue-900/30';
  if (score >= 40) return 'bg-yellow-100 dark:bg-yellow-900/30';
  return 'bg-red-100 dark:bg-red-900/30';
};

const scoreLabel = (score: number) => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  return 'Needs Attention';
};

const riskLabel = (score: number) => {
  if (score >= 70) return { text: 'High Risk', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  if (score >= 40) return { text: 'Moderate', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' };
  return { text: 'Low Risk', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
};

const PHASE_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  data_quality: { label: 'Data Quality', icon: <Shield className="h-4 w-4" /> },
  address_validation: { label: 'Address Validation', icon: <MapPin className="h-4 w-4" /> },
  phone_validation: { label: 'Phone Validation', icon: <Activity className="h-4 w-4" /> },
  email_validation: { label: 'Email Validation', icon: <CheckCircle className="h-4 w-4" /> },
  duplicate_detection: { label: 'Duplicate Detection', icon: <ClipboardCheck className="h-4 w-4" /> },
  census_acs: { label: 'Census ACS Data', icon: <MapPin className="h-4 w-4" /> },
  cdc_svi: { label: 'CDC Social Vulnerability', icon: <Heart className="h-4 w-4" /> },
  food_access: { label: 'Food Access', icon: <MapPin className="h-4 w-4" /> },
  environmental_risk: { label: 'Environmental Risk', icon: <AlertTriangle className="h-4 w-4" /> },
  broadband_access: { label: 'Broadband Access', icon: <Activity className="h-4 w-4" /> },
  transit_access: { label: 'Transit Access', icon: <MapPin className="h-4 w-4" /> },
  program_eligibility: { label: 'Program Eligibility', icon: <ClipboardCheck className="h-4 w-4" /> },
  benefits_cliff: { label: 'Benefits Cliff Analysis', icon: <DollarSign className="h-4 w-4" /> },
  medicaid_eligibility: { label: 'Medicaid Eligibility', icon: <Shield className="h-4 w-4" /> },
  medicare_eligibility: { label: 'Medicare Eligibility', icon: <Shield className="h-4 w-4" /> },
  fhir_clinical: { label: 'Clinical Data (FHIR)', icon: <Heart className="h-4 w-4" /> },
  care_gaps: { label: 'Care Gaps', icon: <AlertCircle className="h-4 w-4" /> },
  risk_stratification: { label: 'Risk Stratification', icon: <TrendingUp className="h-4 w-4" /> },
  credit_check: { label: 'Credit Check', icon: <DollarSign className="h-4 w-4" /> },
  income_estimation: { label: 'Income Estimation', icon: <DollarSign className="h-4 w-4" /> },
  engagement_scoring: { label: 'Engagement Scoring', icon: <Activity className="h-4 w-4" /> },
  comm_preference: { label: 'Communication Preference', icon: <Activity className="h-4 w-4" /> },
  life_event_detection: { label: 'Life Event Detection', icon: <AlertTriangle className="h-4 w-4" /> },
  no_show_prediction: { label: 'No-Show Prediction', icon: <Clock className="h-4 w-4" /> },
  churn_prediction: { label: 'Churn Prediction', icon: <TrendingUp className="h-4 w-4" /> },
  population_risk: { label: 'Population Risk', icon: <BarChart3 className="h-4 w-4" /> },
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
    case 'skipped': return <Clock className="h-4 w-4 text-gray-400" />;
    default: return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
  }
};

export default function MemberEnrichmentTab({ memberId, memberName }: MemberEnrichmentTabProps) {
  const { fmt } = useFormattedDate();
  const [summary, setSummary] = useState<EnrichmentSummary | null>(null);
  const [results, setResults] = useState<EnrichmentResult[]>([]);
  const [retentionScore, setRetentionScore] = useState<RetentionScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [expandedResult, setExpandedResult] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, resultsData, retentionData] = await Promise.allSettled([
        memberEnrichmentAPI.getSummary(memberId),
        memberEnrichmentAPI.getResults(memberId),
        memberEnrichmentAPI.getRetentionScore(memberId),
      ]);
      if (summaryData.status === 'fulfilled') setSummary(summaryData.value);
      if (resultsData.status === 'fulfilled') setResults(resultsData.value.results);
      if (retentionData.status === 'fulfilled') setRetentionScore(retentionData.value);
    } catch (err: any) {
      // 404 = no data yet, 500 = migration not applied yet — both show the "no data" state
      if (err?.response?.status !== 404 && err?.response?.status !== 500) {
        setError('Failed to load enrichment data');
      }
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEnrichNow = async (modules?: string[]) => {
    setEnriching(true);
    try {
      await memberEnrichmentAPI.enrichMember(memberId, modules);
      // Poll for completion
      setTimeout(() => {
        loadData();
        setEnriching(false);
      }, 5000);
    } catch (err: any) {
      setError('Failed to trigger enrichment');
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading enrichment data...</span>
      </div>
    );
  }

  // No enrichment data yet
  if (!summary) {
    return (
      <div className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No enrichment data available yet for this member. Run enrichment to analyze data quality, SDOH risk, eligibility, and more.
          </AlertDescription>
        </Alert>
        <Button onClick={() => handleEnrichNow()} disabled={enriching} className="gap-2">
          {enriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {enriching ? 'Enriching...' : 'Run Full Enrichment'}
        </Button>
      </div>
    );
  }

  const dimensions = [
    { key: 'data_quality_score', label: 'Data Quality', icon: <Shield className="h-5 w-5" />, score: summary.data_quality_score },
    { key: 'contact_validity_score', label: 'Contact Validity', icon: <CheckCircle className="h-5 w-5" />, score: summary.contact_validity_score },
    { key: 'sdoh_risk_score', label: 'SDOH Risk', icon: <Heart className="h-5 w-5" />, score: summary.sdoh_risk_score, isRisk: true },
    { key: 'clinical_risk_score', label: 'Clinical Risk', icon: <Activity className="h-5 w-5" />, score: summary.clinical_risk_score, isRisk: true },
    { key: 'engagement_score', label: 'Engagement', icon: <TrendingUp className="h-5 w-5" />, score: summary.engagement_score },
    { key: 'financial_risk_score', label: 'Financial Risk', icon: <DollarSign className="h-5 w-5" />, score: summary.financial_risk_score, isRisk: true },
    { key: 'eligibility_score', label: 'Eligibility', icon: <ClipboardCheck className="h-5 w-5" />, score: summary.eligibility_score },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Header with Overall Score and Enrich Now */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex items-center justify-center w-16 h-16 rounded-full ${scoreBg(summary.overall_score)}`}>
            <span className={`text-2xl font-bold ${scoreColor(summary.overall_score)}`}>
              {Math.round(summary.overall_score)}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Member Data Enrichment Score
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {scoreLabel(summary.overall_score)} — {summary.total_modules_run} modules run, {summary.enrichment_completeness.toFixed(0)}% complete
            </p>
            {summary.last_enriched_at && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Last enriched: {fmt.dateTime(summary.last_enriched_at)}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData()}
            disabled={loading}
            className="gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => handleEnrichNow()}
            disabled={enriching}
            className="gap-1"
          >
            {enriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {enriching ? 'Running...' : 'Enrich Now'}
          </Button>
        </div>
      </div>

      {/* Dimension Score Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {dimensions.map((dim) => {
          const risk = dim.isRisk ? riskLabel(dim.score) : null;
          return (
            <Card key={dim.key} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={scoreColor(dim.isRisk ? 100 - dim.score : dim.score)}>{dim.icon}</span>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">{dim.label}</span>
                </div>
                <div className="flex items-end justify-between">
                  <span className={`text-2xl font-bold ${scoreColor(dim.isRisk ? 100 - dim.score : dim.score)}`}>
                    {Math.round(dim.score)}
                  </span>
                  {risk ? (
                    <Badge className={`text-xs ${risk.color}`}>{risk.text}</Badge>
                  ) : (
                    <Badge className={`text-xs ${scoreBg(dim.score)} ${scoreColor(dim.score)}`}>
                      {scoreLabel(dim.score)}
                    </Badge>
                  )}
                </div>
                {/* Score bar */}
                <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      dim.score >= 80 ? 'bg-green-500' : dim.score >= 60 ? 'bg-blue-500' : dim.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, dim.score)}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Retention Intelligence (from retention scoring engine) */}
      {retentionScore && (
        <Card className="border-l-4 border-indigo-300 dark:border-indigo-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" /> Retention Intelligence
              <Badge variant="outline" className="text-xs ml-1">from retention engine</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <span className="text-xs text-gray-500">Engagement</span>
                <p className={`text-xl font-bold ${scoreColor(retentionScore.engagement_score)}`}>
                  {Math.round(retentionScore.engagement_score)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Risk Level</span>
                <p className="text-sm font-medium capitalize mt-1">
                  <Badge className={
                    retentionScore.risk_level === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    retentionScore.risk_level === 'at_risk' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  }>
                    {retentionScore.risk_level?.replace(/_/g, ' ') || 'Unknown'}
                  </Badge>
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Trend</span>
                <p className="text-sm font-medium mt-1">
                  {retentionScore.trend_direction === 'improving' && <span className="text-green-600">Improving</span>}
                  {retentionScore.trend_direction === 'declining' && <span className="text-red-600">Declining</span>}
                  {retentionScore.trend_direction === 'stable' && <span className="text-gray-600">Stable</span>}
                  {!retentionScore.trend_direction && <span className="text-gray-400">N/A</span>}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Churn Risk</span>
                <p className={`text-xl font-bold ${
                  (retentionScore.churn_probability || 0) >= 60 ? 'text-red-600' :
                  (retentionScore.churn_probability || 0) >= 30 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {retentionScore.churn_probability != null ? `${Math.round(retentionScore.churn_probability)}%` : 'N/A'}
                </p>
              </div>
            </div>
            {retentionScore.days_since_last_contact != null && (
              <p className="text-xs text-gray-400 mt-2">
                Last contact: {retentionScore.days_since_last_contact} days ago
                {retentionScore.calculated_at && ` · Scored: ${fmt.short(retentionScore.calculated_at)}`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Enrichment Changes for Review */}
      <ChangeReviewPanel memberId={memberId} onChangesApplied={loadData} />

      {/* Risk Flags, Care Gaps, SDOH Needs, Eligible Programs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Risk Flags */}
        {summary.risk_flags && summary.risk_flags.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" /> Risk Flags
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {summary.risk_flags.map((flag: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {flag.type?.replace(/_/g, ' ')}
                      {flag.probability && ` (${flag.probability}%)`}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Care Gaps */}
        {summary.care_gaps && summary.care_gaps.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-500" /> Care Gaps
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {summary.care_gaps.map((gap: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {typeof gap === 'string' ? gap : gap.measure || gap.type || JSON.stringify(gap)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* SDOH Needs */}
        {summary.sdoh_needs && summary.sdoh_needs.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Heart className="h-4 w-4 text-purple-500" /> SDOH Needs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {summary.sdoh_needs.map((need: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {typeof need === 'string' ? need : need.category || need.type || JSON.stringify(need)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Eligible Programs */}
        {summary.eligible_programs && summary.eligible_programs.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-green-500" /> Eligible Programs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {summary.eligible_programs.map((prog: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {typeof prog === 'string' ? prog : prog.name || prog.program_name || JSON.stringify(prog)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Issues Summary */}
      {summary.total_issues_found > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {summary.total_issues_found} issue{summary.total_issues_found !== 1 ? 's' : ''} found across enrichment modules. Review the detailed results below.
          </AlertDescription>
        </Alert>
      )}

      {/* Detailed Module Results */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowResults(!showResults)}>
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Module Results ({results.length})
            </span>
            {showResults ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </CardTitle>
        </CardHeader>
        {showResults && (
          <CardContent className="pt-0">
            {results.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No module results yet.</p>
            ) : (
              <div className="space-y-2">
                {results.map((r) => {
                  const moduleInfo = PHASE_LABELS[r.module_key] || { label: r.module_key.replace(/_/g, ' '), icon: <Brain className="h-4 w-4" /> };
                  const isExpanded = expandedResult === r.id;
                  return (
                    <div
                      key={r.id}
                      className="border rounded-lg overflow-hidden dark:border-gray-700"
                    >
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        onClick={() => setExpandedResult(isExpanded ? null : r.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {statusIcon(r.status)}
                          <span className="text-gray-500">{moduleInfo.icon}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {moduleInfo.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {r.confidence_score !== null && (
                            <span className={`text-sm font-medium ${scoreColor(r.confidence_score * 100)}`}>
                              {(r.confidence_score * 100).toFixed(0)}%
                            </span>
                          )}
                          {r.fields_enriched > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {r.fields_enriched} enriched
                            </Badge>
                          )}
                          {r.issues_found && r.issues_found.length > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {r.issues_found.length} issues
                            </Badge>
                          )}
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 text-xs">
                            <div>
                              <span className="text-gray-500">Status</span>
                              <p className="font-medium capitalize">{r.status}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Confidence</span>
                              <p className="font-medium">{r.confidence_score !== null ? `${(r.confidence_score * 100).toFixed(1)}%` : 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Duration</span>
                              <p className="font-medium">{r.duration_ms ? `${r.duration_ms}ms` : 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Cost</span>
                              <p className="font-medium">{r.cost_usd > 0 ? `$${r.cost_usd.toFixed(4)}` : 'Free'}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-xs pb-3">
                            <div>
                              <span className="text-gray-500">Fields Enriched</span>
                              <p className="font-medium">{r.fields_enriched}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fields Validated</span>
                              <p className="font-medium">{r.fields_validated}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fields Corrected</span>
                              <p className="font-medium">{r.fields_corrected}</p>
                            </div>
                          </div>

                          {/* Module-specific metadata */}
                          {r.metadata && r.module_key === 'email_validation' && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Validation Details</p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-500">Domain</span>
                                  <p className="font-medium">{r.metadata.domain || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Domain Type</span>
                                  <p className="font-medium capitalize">{(r.metadata.domain_type || 'unknown').replace('_', ' ')}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">MX Verified</span>
                                  <p className="font-medium">{r.metadata.mx_verified ? 'Yes' : 'No'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Disposable</span>
                                  <p className={`font-medium ${r.metadata.is_disposable ? 'text-red-600' : 'text-green-600'}`}>
                                    {r.metadata.is_disposable ? 'Yes' : 'No'}
                                  </p>
                                </div>
                              </div>
                              {r.metadata.validation_layers && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {r.metadata.validation_layers.map((layer: string, i: number) => (
                                    <Badge key={i} variant="outline" className="text-[10px]">{layer}</Badge>
                                  ))}
                                </div>
                              )}
                              {r.metadata.zerobounce && (
                                <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                  ZeroBounce: <span className="font-medium capitalize">{r.metadata.zerobounce.status}</span>
                                  {r.metadata.zerobounce.did_you_mean && (
                                    <span className="ml-2 text-blue-600">Did you mean: {r.metadata.zerobounce.did_you_mean}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {r.metadata && r.module_key === 'duplicate_detection' && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Duplicate Detection — {r.metadata.duplicates_found || 0} potential match{(r.metadata.duplicates_found || 0) !== 1 ? 'es' : ''}
                                {r.metadata.fuzzy_matching_available !== null && (
                                  <span className="ml-2 text-gray-400">
                                    ({r.metadata.fuzzy_matching_available ? 'Fuzzy + Exact' : 'Exact only'})
                                  </span>
                                )}
                              </p>
                              {r.metadata.duplicates && r.metadata.duplicates.length > 0 && (
                                <div className="space-y-1.5">
                                  {r.metadata.duplicates.map((dup: any, i: number) => (
                                    <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-gray-50 dark:bg-gray-700/50">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{dup.name}</span>
                                        <span className="text-gray-400">ID: {dup.member_id}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="flex gap-1">
                                          {(dup.match_signals || [dup.match_type]).map((sig: string, j: number) => (
                                            <Badge key={j} variant="outline" className="text-[10px]">{sig.replace(/_/g, ' ')}</Badge>
                                          ))}
                                        </div>
                                        <span className={`font-medium ${dup.confidence >= 0.85 ? 'text-red-600' : dup.confidence >= 0.65 ? 'text-yellow-600' : 'text-gray-600'}`}>
                                          {(dup.confidence * 100).toFixed(0)}%
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {r.metadata.methodology && (
                                <p className="text-[10px] text-gray-400 mt-1.5">
                                  Method: {r.metadata.methodology.replace(/_/g, ' ')} | Formula: {r.metadata.composite_formula}
                                </p>
                              )}
                            </div>
                          )}

                          {r.metadata && r.module_key === 'data_quality' && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Quality Tier: <span className="capitalize">{r.metadata.quality_tier || 'N/A'}</span>
                              </p>
                              {r.scores && (
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
                                  {Object.entries(r.scores).map(([key, val]) => (
                                    <div key={key}>
                                      <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                      <p className={`font-medium ${scoreColor(val as number)}`}>{(val as number).toFixed(0)}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {r.metadata.missing_fields && r.metadata.missing_fields.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  <span className="text-[10px] text-gray-500 mr-1">Missing:</span>
                                  {r.metadata.missing_fields.map((f: string, i: number) => (
                                    <Badge key={i} variant="outline" className="text-[10px] text-yellow-700 dark:text-yellow-400">{f.replace(/_/g, ' ')}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {r.metadata && (r.module_key === 'census_acs' || r.module_key === 'cdc_svi') && r.metadata.sdoh_needs && r.metadata.sdoh_needs.length > 0 && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">SDOH Needs Identified</p>
                              <div className="space-y-1">
                                {r.metadata.sdoh_needs.map((need: any, i: number) => (
                                  <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="capitalize">{(need.domain || '').replace(/_/g, ' ')}: {(need.indicator || '').replace(/_/g, ' ')}</span>
                                    {need.value !== undefined && <span className="text-gray-500">{need.value}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {r.metadata && r.module_key === 'address_validation' && r.metadata.dpv_confirmation && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700 text-xs">
                              <span className="text-gray-500">USPS DPV: </span>
                              <span className="font-medium">{
                                {Y: 'Confirmed', D: 'Confirmed (missing secondary)', S: 'Secondary missing', N: 'Not confirmed'}[r.metadata.dpv_confirmation as string] || r.metadata.dpv_confirmation
                              }</span>
                              {r.metadata.zip4 && <span className="ml-2 text-gray-400">ZIP+4: {r.metadata.zip4}</span>}
                            </div>
                          )}

                          {r.metadata && r.module_key === 'phone_validation' && (
                            <div className="mb-3 p-2 rounded bg-white dark:bg-gray-800 border dark:border-gray-700 text-xs">
                              <div className="flex gap-4">
                                <div><span className="text-gray-500">Valid: </span><span className={`font-medium ${r.metadata.valid ? 'text-green-600' : 'text-red-600'}`}>{r.metadata.valid ? 'Yes' : 'No'}</span></div>
                                <div><span className="text-gray-500">Type: </span><span className="font-medium capitalize">{r.metadata.line_type || 'N/A'}</span></div>
                                {r.metadata.carrier && <div><span className="text-gray-500">Carrier: </span><span className="font-medium">{r.metadata.carrier}</span></div>}
                              </div>
                            </div>
                          )}

                          {/* Scores bar (for modules with scores) */}
                          {r.scores && !['data_quality'].includes(r.module_key) && Object.keys(r.scores).length > 0 && (
                            <div className="mb-2">
                              <div className="flex gap-3 text-xs">
                                {Object.entries(r.scores).map(([key, val]) => (
                                  <div key={key}>
                                    <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}: </span>
                                    <span className={`font-medium ${scoreColor(val as number)}`}>{(val as number).toFixed(0)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {r.error_message && (
                            <Alert variant="destructive" className="mb-2">
                              <AlertDescription className="text-xs">{r.error_message}</AlertDescription>
                            </Alert>
                          )}
                          {r.issues_found && r.issues_found.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 mb-1">Issues</p>
                              {r.issues_found.map((issue: any, i: number) => (
                                <div key={i} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1 mb-0.5">
                                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  {typeof issue === 'string' ? issue : (
                                    issue.message || issue.description ||
                                    `${(issue.field || '').replace(/_/g, ' ')}: ${(issue.issue || '').replace(/_/g, ' ')}${issue.severity ? ` (${issue.severity})` : ''}${issue.value ? ` — ${issue.value}` : ''}`
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {r.suggestions && r.suggestions.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Suggestions</p>
                              {r.suggestions.map((sug: any, i: number) => (
                                <div key={i} className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1 mb-0.5">
                                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  {typeof sug === 'string' ? sug : sug.message || sug.action || `${(sug.field || '').replace(/_/g, ' ')}: ${sug.priority || ''}`}
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {r.external_source && `Source: ${r.external_source} · `}
                            Run: {fmt.dateTime(r.created_at)}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export { MemberEnrichmentTab };
